import { useEffect, useRef, type RefObject } from 'react';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

type UseSheetDismissalOptions = {
  containerRef: RefObject<HTMLElement | null>;
  onClose: () => void;
};

/**
 * Modal behaviour of the mobile date sheet (CMS-1854 §3, a11y edge cases):
 * focus is trapped inside it and restored on close, and Escape closes the
 * sheet rather than the surrounding TIOptions modal.
 *
 * The sheet is mounted only while open, so mount/unmount is the open/close
 * signal — no `isOpen` flag needed.
 *
 * Closing on the OS back gesture is deliberately NOT handled here. Driving it
 * from `history.pushState` broke the sheet outright: React StrictMode invokes
 * mount effects twice, so the cleanup's asynchronous `history.back()` landed on
 * the listener the second invocation had just registered and closed the sheet
 * in the same frame. Raw `pushState` for UI state is also against the repo's
 * convention — the sanctioned route is `useCmsHashState`, which owns the state
 * in the URL hash (see `useApplyV3Modal`) and would mean moving `isOpen` out of
 * this component.
 */
export function useSheetDismissal({
  containerRef,
  onClose,
}: UseSheetDismissalOptions): void {
  // Latest-ref so the listeners can stay mount-scoped while still calling the
  // current handler.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const container = containerRef.current;
    const previouslyFocused = document.activeElement;

    container
      ?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)
      ?.focus({ preventScroll: true });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // Capture phase + stopPropagation: the surrounding TIOptions modal
        // listens for Escape on `document` too, and closing the whole modal
        // instead of just the sheet would discard the trip details.
        event.stopPropagation();
        onCloseRef.current();
        return;
      }

      if (event.key !== 'Tab' || !container) {
        return;
      }

      // Only tabbable elements bound the trap. The day grid is one roving
      // tab stop: every day button except the active one carries
      // `tabindex="-1"`, so taking them as the boundary would put the last
      // stop on a day the user can never Tab to, and Tab would walk out of
      // the sheet into the page behind it.
      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((element) => element.tabIndex >= 0);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!first || !last) {
        return;
      }

      // Focus outside the container (e.g. a click on the title, the guidance
      // plate or the summary text blurred to `body`) must be pulled back in —
      // otherwise native Tab resumes from the top of the document, i.e. the
      // page behind the sheet, since the portal root is the last child of
      // `body`. Treat it as the boundary in the tab direction.
      if (!container.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);

      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, [containerRef]);
}
